export enum PvpCategory {
  Black = 'black',
  Red = 'red',
  Yellow = 'yellow',
  Blue = 'blue',
}

export const allPvpCategories = Object.values(PvpCategory);

export enum MapCategory {
  Arena = 'arena',
  Corrupted = 'corrupted',
  Dungeon = 'dungeon',
  Island = 'island',
  Expedition = 'expedition',
  Hideout = 'hideout',
  Openworld = 'openworld',
  Passage = 'passage',
  City = 'city',
  Roads = 'roads',
  Other = 'other',
}

export const validMapCategories: MapCategory[] = [MapCategory.City, MapCategory.Dungeon, MapCategory.Passage, MapCategory.Openworld];

export const allMapCategories = Object.values(MapCategory);

export enum PortalKind {
  Thetford = 'Thetford',
  Lymhurst = 'Lymhurst',
  Bridgewatch = 'Bridgewatch',
  Martlock = 'Martlock',
  FortSterling = 'FortSterling',
}

export const allPortalKinds = Object.values(PortalKind);

export type PvpCategoryColors = Record<PvpCategory, string>;
export type MapCategoryColors = Record<MapCategory, string>;

export type PvpCategoryWeights = Record<PvpCategory, number>;
export type MapCategoryWeights = Record<MapCategory, number>;

export const defaultPvpCategoryWeights: PvpCategoryWeights = {
  black: 1.5,
  red: 1.2,
  yellow: 1,
  blue: 1,
};

export const defaultMapCategoryWeights: MapCategoryWeights = {
  arena: 1000,
  corrupted: 1000,
  dungeon: 3,
  island: 1000,
  expedition: 1000,
  hideout: 100,
  openworld: 1,
  passage: 1.2,
  city: 1,
  roads: 1000,
  other: 1000,
};

export class PathfinderConfiguration {
  portalKinds: PortalKind[] = [];
  pvpCategoryWeights: Record<PvpCategory, number> = defaultPvpCategoryWeights;
  mapCategoryWeights: Record<MapCategory, number> = defaultMapCategoryWeights;
  mapsToAvoid: string[] = [];
  speed: number = 12;

  constructor() { }

  static fromJson(json: string): PathfinderConfiguration {
    const plainObject = JSON.parse(json);

    const result: PathfinderConfiguration = Object.assign(new PathfinderConfiguration(), plainObject);

    return result;
  }
}

export const pvpCategoryColors: PvpCategoryColors = {
  black: '#2d2d2dcc',
  red: '#e63946cc',
  yellow: '#ffd60acc',
  blue: '#3a86ffcc',
};

export const mapCategoryColors: MapCategoryColors = {
  arena: 'blue',
  corrupted: 'blue',
  dungeon: 'red',
  island: 'blue',
  expedition: 'blue',
  hideout: 'blue',
  openworld: 'blue',
  passage: 'green',
  city: 'white',
  roads: 'blue',
  other: 'blue',
};

export interface Cluster {
  id: string;
  imageFile: string;
  displayName: string;
  tier: number;
  quality?: number;
  type: string;
  pvpCategory: PvpCategory;
  mapCategory: MapCategory;
  origin?: { x: number; y: number };
  size?: { x: number; y: number };
  worldmapposition?: { x: number; y: number };
  minimapBoundsMin?: { x: number; y: number };
  minimapBoundsMax?: { x: number; y: number };
  neighbours: Set<string>;
  exits: Exit[];
  portalExits: Exit[];
  portalEntrances: Exit[];
  mapCenter?: { x: number; y: number };
  key?: string;
  isSmugglersNetworkMarket: boolean;
  isMarket: boolean;
}

export interface Exit {
  id: string;
  targetId: string;
  targetLocationId: string;
  targetDisplayName: string;
  targetMapCategory: MapCategory;
  kind?: string;
  position?: { x: number; y: number };
}

export interface ClusterInfo {
  step: number;
  currentClusterId: string;
  currentCluster: Cluster;
  entry: { x: number; y: number; exit: Exit | undefined };
  exit?: { x: number; y: number; exit: Exit | undefined };
  distance: number;
  cost: number;
  priorCluster: Cluster | undefined;
  nextCluster: Cluster | undefined;
}
export interface QueueItem {
  currentClusterId: string;
  entry: { x: number; y: number; exit: Exit | undefined };
  path: Array<{ clusterId: string; entry: { x: number; y: number; exit: Exit | undefined } }>;
  exits: Array<{ x: number; y: number; exit: Exit | undefined }>;
}

export class PriorityQueue<T> {
  private elements: [number, T][] = [];

  enqueue(element: [number, T]): void {
    this.elements.push(element);
    this.elements.sort((a, b) => a[0] - b[0]);
  }

  dequeue(): [number, T] | undefined {
    return this.elements.shift();
  }

  isEmpty(): boolean {
    return this.elements.length === 0;
  }
}
